// ---------------------------------------------- modules import
import M from "materialize-css";
import React, {
  ChangeEvent,
  FormEvent,
  FunctionComponent,
  useEffect
} from "react";
import { Link, useParams } from "react-router-dom";

import { IUpdateFormProps } from "./common";
import { useAuth, useUser } from "./hooks";
import { ThemeContext } from "../../../contexts/themeContext";
import PermissionHelper, {
  MenuIndex
} from "../../../store/helpers/permissionHelper";
import TagHelper from "../../../store/helpers/tagHelper";
import { pattern } from "../../../store/helpers/validationHelper";

// ---------------------------------------------- the component
const UpdateForm: FunctionComponent<IUpdateFormProps> = ({ menuIndex }) => {
  // ---------------------------------------------- router state
  const { user_id } = useParams();

  // ---------------------------------------------- local state
  const { auth, profile } = useAuth();
  const { user, isUserValid, onChange, onUpdate } = useUser(menuIndex, user_id);

  const tags = TagHelper.toTags(user.tags);
  const menuGroups = PermissionHelper.toMenuGroups(user.permissions);

  // ---------------------------------------------- handlers
  const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
    onChange(
      e.currentTarget.name,
      e.currentTarget.value,
      e.currentTarget.className
    );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    onUpdate(auth, profile, `/${PermissionHelper.getUrl(MenuIndex.User)}`);
  };

  // ---------------------------------------------- effects
  useEffect(() => {
    const collapsibles = document.querySelectorAll(".collapsible");

    if (collapsibles) M.Collapsible.init(collapsibles);
  }, []);

  useEffect(() => {
    const modals = document.querySelectorAll(".modal");

    M.Modal.init(modals);
  }, []);

  // ---------------------------------------------- content
  return profile ? (
    <ThemeContext.Consumer>
      {({ theme }) => (
        <div className="container row">
          <form className="card col s12 m10 offset-m1" onSubmit={handleSubmit}>
            <div
              className="card-content row"
              style={{ marginBottom: "0px", paddingBottom: "0px" }}
            >
              <span style={{ fontSize: "0.75rem" }}>
                {PermissionHelper.getName(MenuIndex.User).toLowerCase()}
              </span>

              <span className="card-title" style={{ marginBottom: "20px" }}>
                UPDATE FORM
              </span>

              <div className="input-field  col s12 m6">
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="validate"
                  pattern={pattern.user.name.toString().slice(1, -1)}
                  minLength={2}
                  maxLength={40}
                  value={user.name}
                  onChange={handleChange}
                  required
                />

                <label className="active" htmlFor="name">
                  Name
                </label>
              </div>

              <div className="input-field col s12 m6">
                <input
                  type="text"
                  id="email"
                  className="validate"
                  pattern={pattern.login.email.toString().slice(1, -1)}
                  minLength={2}
                  maxLength={200}
                  value={user.email}
                  disabled
                  required
                />

                <label className="active" htmlFor="email">
                  Email
                </label>
              </div>

              <div className="input-field col s12" style={{ padding: "0px" }}>
                <div className="row" style={{ margin: "0px", padding: "0px" }}>
                  {tags.map(tag => (
                    <div
                      key={tag.index}
                      className="col s6 row"
                      style={{ margin: "0px", padding: "0px" }}
                    >
                      <div className="col s3">
                        <label>
                          <input
                            type="checkbox"
                            name="tags"
                            className="filled-in"
                            value={tag.index}
                            checked={tag.tagged}
                            onChange={handleChange}
                          />

                          <span />
                        </label>
                      </div>

                      <div
                        className="col s9"
                        style={{ fontSize: "0.65rem" }}
                      >{`#${tag.shortName.toLowerCase()}`}</div>
                    </div>
                  ))}
                </div>
              </div>

              {menuGroups.map((menuGroup, index) => (
                <div
                  key={index}
                  className="input-field col s12 m6"
                  style={{ marginBottom: "0px", padding: "0px" }}
                >
                  <ul className="collapsible">
                    <li>
                      <div className="collapsible-header waves-effect">
                        <i
                          className="material-icons"
                          style={{ color: theme.icon }}
                        >
                          {menuGroup.icon}
                        </i>
                        {menuGroup.name}
                      </div>
                      <div
                        className="collapsible-body"
                        style={{ fontSize: "0.7em" }}
                      >
                        <table>
                          <tbody style={{ paddingLeft: "1rem" }}>
                            <tr>
                              <td className="center-align">{menuGroup.name}</td>

                              <td className="center-align">View</td>

                              <td className="center-align">Modify</td>
                            </tr>

                            {menuGroup.menus.map(menu => (
                              <tr key={menu.index}>
                                <td>{menu.shortName}</td>

                                <td className="center-align">
                                  <label>
                                    <input
                                      type="checkbox"
                                      name="permissions"
                                      className="filled-in view"
                                      value={menu.index}
                                      checked={menu.permission.view}
                                      onChange={handleChange}
                                      disabled={
                                        !PermissionHelper.getView(
                                          profile.permissions,
                                          menu.index
                                        ) ||
                                        menu.index === MenuIndex.GeneralPost
                                      }
                                    />

                                    <span />
                                  </label>
                                </td>

                                <td className="center-align">
                                  <label>
                                    <input
                                      type="checkbox"
                                      name="permissions"
                                      className="filled-in modify"
                                      value={menu.index}
                                      checked={menu.permission.modify}
                                      onChange={handleChange}
                                      disabled={
                                        !PermissionHelper.getModify(
                                          profile.permissions,
                                          menu.index
                                        )
                                      }
                                    />

                                    <span />
                                  </label>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </li>
                  </ul>
                </div>
              ))}
            </div>

            <div className="card-action row" style={{ marginBottom: "0" }}>
              <div className="col s6">
                <Link
                  to={`/${PermissionHelper.getUrl(MenuIndex.User)}`}
                  className="btn-floating btn-small waves-effect waves-light"
                  style={{ backgroundColor: theme.ui }}
                >
                  <i className="material-icons left">arrow_back</i>
                </Link>
              </div>

              <div className="col s6 right-align">
                <button
                  type="button"
                  className={`btn-small waves-effect waves-light modal-trigger ${
                    isUserValid() ? "" : "disabled"
                  }`}
                  style={{ backgroundColor: theme.ui }}
                  data-target="alert"
                >
                  Submit<i className="material-icons right">send</i>
                </button>

                <div className="modal" id="alert">
                  <div className="modal-content center">
                    <h5>Confirm Update</h5>

                    <p>{`Are you sure you want to update this ${PermissionHelper.getName(
                      MenuIndex.User
                    ).toLowerCase()}?`}</p>
                  </div>

                  <div className="modal-footer">
                    <div className="container">
                      <button
                        type="submit"
                        className="btn modal-close"
                        style={{ backgroundColor: theme.ui }}
                      >
                        Yes, I'm sure
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </ThemeContext.Consumer>
  ) : null;
};

export default UpdateForm;
